/**
 * How many times one cast actually applies its effects.
 *
 * A persistent ground area is not one hit. `advanceArea` (`sim/tick-scheduler.ts`)
 * re-evaluates the area on a schedule and re-applies the skill's whole effect
 * list to every target that has passed its per-target re-hit gate
 * (`tick - lastHitTick >= hitIntervalTicks`), for the area's entire lifetime.
 * The evaluator priced all of that as a single application, so a 3s pulse area
 * firing every 0.5s was valued at exactly the price of an instant hit while the
 * runtime delivered six.
 *
 * The empirical burst path already saw this — it runs the real harness, so a
 * long or dense area trips `POST_NUMERIC_BURST_CAP_EXCEEDED`. Only the analytic
 * SVP path was blind to it, which is why an area could be simultaneously
 * burst-legal and grossly under-priced.
 *
 * Mirrors the runtime rather than modeling it: the schedule below is read off
 * `authoritative-world.ts`'s `onPersistentArea` wiring and `advanceArea`'s gate.
 */

import { persistentAreaApplicationTicks } from '../sim/tick-scheduler.js';
import { dotTickCount } from '../math.js';
import { runtimeGateProbability, mapRuntimeRollIds, effectApplicationTimeline, type RuntimeRoll, periodicCadence, maintainedEffectRatio, MAX_STATUS_APPLICATIONS, type EffectApplication } from './status-timeline.js';
import { DEFAULT_AREA_INTERVAL_S, type Delivery } from '../types.js';

export type ApplicationGate = RuntimeRoll;

export interface ApplicationSchedule {
  /** Times, in seconds after cast, at which the effect list is applied. */
  readonly offsetsS: readonly number[];
  /** Attack-unit ancestry and pulse order, taken from the runtime effect tree. */
  readonly orders?: readonly (readonly number[])[];
  /** Rolls evaluated before each application; descendants retain the same roll identity. */
  readonly gates?: readonly (readonly ApplicationGate[])[];
}

const INSTANT_SCHEDULE: ApplicationSchedule = Object.freeze({ offsetsS: Object.freeze([0]) });

/**
 * Guards against a degenerate interval turning into an unbounded impulse list.
 * The runtime cannot fire more often than one server tick either, so a schedule
 * beyond this bound is a fixture error, not a skill.
 */
export const MAX_APPLICATIONS = MAX_STATUS_APPLICATIONS;

/**
 * The application schedule one cast of `delivery` produces.
 *
 * Non-persistent deliveries apply once, at cast time — the existing behavior,
 * returned unchanged so instant skills price exactly as before.
 *
 * A persistent area's first evaluation is scheduled at `tick + interval`, not at
 * the spawn tick (`authoritative-world.ts` schedules the PULSE/REPEAT event at
 * `this.tick + interval`), so offsets start at one interval and run to the end
 * of the area's lifetime. An area that expires before its first interval
 * delivers nothing (`expireAttack` removes it first), so its schedule is empty.
 */
export function deliveryApplicationSchedule(delivery: Delivery, hostTickS?: number): ApplicationSchedule {
  if (delivery.type !== 'ground_aoe' || delivery.durationS === undefined) return INSTANT_SCHEDULE;
  const durationS = delivery.durationS;
  if (!Number.isFinite(durationS)) return INSTANT_SCHEDULE;

  // PULSE areas re-fire on their declared pulse interval. CONTINUOUS areas are
  // re-evaluated on the collision-check interval but each target is still gated
  // by the hit interval, so the slower of the two governs how often a given
  // target can be struck.
  const rawIntervalS = delivery.pulseIntervalS !== undefined
    ? delivery.pulseIntervalS
    : Math.max(delivery.collisionCheckIntervalS ?? DEFAULT_AREA_INTERVAL_S, DEFAULT_AREA_INTERVAL_S);
  if (!Number.isFinite(rawIntervalS) || rawIntervalS <= 0) return INSTANT_SCHEDULE;
  if (hostTickS !== undefined) {
    if (!(hostTickS > 0) || !Number.isFinite(hostTickS) || !Number.isFinite(durationS / hostTickS) || !Number.isFinite(rawIntervalS / hostTickS)) return INSTANT_SCHEDULE;
    return { offsetsS: persistentAreaApplicationTicks(durationS, delivery.pulseIntervalS, delivery.collisionCheckIntervalS, 1 / hostTickS, MAX_APPLICATIONS).map((tick) => tick * hostTickS) };
  }
  if (durationS <= 0) return INSTANT_SCHEDULE;

  const intervalS = rawIntervalS;
  const count = Math.min(MAX_APPLICATIONS, Math.floor(durationS / intervalS + 1e-9));

  const offsetsS: number[] = [];
  for (let k = 1; k <= count; k++) offsetsS.push(k * intervalS);
  return { offsetsS };
}

/** From the first application to the last: the span a re-applied strongest-wins status is held over, beyond one duration. */
export function applicationSpanS(offsetsS: readonly number[]): number {
  return offsetsS.length === 0 ? 0 : offsetsS[offsetsS.length - 1]! - offsetsS[0]!;
}

/** Fraction of stacked status-seconds actually active: overlapping applications hold one status; gaps stay empty. */
export function maintainedApplicationRatio(durationS: number, offsetsS: readonly number[] = [0]): number {
  return maintainedEffectRatio(durationS, offsetsS);
}

/**
 * How an effect kind's value behaves when the delivery applies it repeatedly.
 *
 * Read off the runtime, not chosen. `addStatus` (`sim/runtime.ts`) appends an
 * instance per application, except a damage over time, which `refreshStatus`
 * replaces; so what matters is how each kind's *reader* aggregates the
 * resulting overlapping instances.
 *
 * **Scoped to Beta V1 (BETA.SCOPE.2, `docs/gameplay/README.md`).** Beta V1 is
 * damage-centered: direct damage and DoT are core, and bounded offensive
 * CC/displacement is required coverage only where it rides on a damage-bearing
 * skill. Pure sustain/support/self-buff/resource utility is out of scope, so
 * those kinds are deliberately absent here rather than classified — they fall
 * to the default and are left priced exactly as they are today.
 */
export type OverlapModel =
  /** Each application is an independent instance whose value adds. */
  | 'ACCUMULATES'
  /** Overlapping instances read as one; re-application extends the window. */
  | 'MAINTAINED'
  /** Re-application replaces the status and keeps its tick schedule: one dot, ticking from the first application to one duration after the last. */
  | 'REFRESHED'
  /** Not classified: repetition changes nothing about this kind's price here. */
  | 'UNPRICED';

const OVERLAP_MODELS: Readonly<Record<string, OverlapModel>> = {
  // Beta V1 core. A damage hit lands once per application, so N applications
  // really are N times the effect.
  damage: 'ACCUMULATES',
  // A source's dot on a target is one status however many applications reach it (`sim/runtime.ts` `refreshStatus`).
  dot: 'REFRESHED',

  // Bounded displacement — Beta V1 coverage when carried by a damage-bearing
  // skill. Each shove is a fresh impulse, so these add.
  knockback: 'ACCUMULATES',
  pull: 'ACCUMULATES',

  // Bounded offensive CC — same Beta V1 condition. Every reader takes the
  // strongest instance rather than summing, so six overlapping 1s stuns are not
  // twelve stun-seconds; they are one lock held until the last expires:
  //   stun/root/silence/disarm  `.some()` gates          (sim/cc-gating.ts)
  //   slow                      `strongestSlowFraction`  (sim/state.ts, Math.max)
  // Magnitude is therefore unchanged by repetition; only the window grows.
  // (`silence`/`disarm` have no ratified SVP price yet and fail closed before
  // reaching this, but they are in-scope CC and classified with their family.)
  stun: 'MAINTAINED',
  root: 'MAINTAINED',
  silence: 'MAINTAINED',
  disarm: 'MAINTAINED',
  slow: 'MAINTAINED',
};

export function overlapModel(kind: string): OverlapModel {
  return OVERLAP_MODELS[kind] ?? 'UNPRICED';
}

export function accumulatesPerApplication(kind: string): boolean {
  return overlapModel(kind) === 'ACCUMULATES';
}

/** A dot's cadence: one tick every `tickS` for `durationS`. */
export interface DotCadence {
  readonly tickS: number;
  readonly durationS: number;
}

/**
 * When a dot that is applied at `offsetsS` (seconds after the cast, ascending)
 * to one target deals its ticks: an application while the dot is running
 * restarts its duration and leaves its tick schedule alone, one after it ran
 * out starts a schedule of its own.
 */
export function refreshedDotTickTimesS(offsetsS: readonly number[], cadence: DotCadence, hostTickS?: number): number[] {
  return effectApplicationTimeline([offsetsS.map((atS) => ({ atS, share: 1 }))], cadence.durationS, cadence.tickS, hostTickS).ticks.map(({ atS }) => atS);
}

/** One release of a stage's attack at `atS` seconds after the stage's own release; `share` is how much of an application reaches the target (1 for an attack unit, an orbit contact bucket's expected contacts). */
export type DotRelease = EffectApplication;

/**
 * When the units of a static stage release, grouped by the footprint they share:
 * units of one footprint (a volley's repeats, coincident members) reach the same
 * targets, so a dot they apply is one dot (`dotRefreshRatio`).
 */
export function releaseGroupsOfUnits(units: readonly { readonly releaseOffsetTicks: number; readonly transforms: unknown }[], tickS: number): readonly (readonly DotRelease[])[] {
  const groups = new Map<string, DotRelease[]>();
  for (const [unitIndex, unit] of units.entries()) {
    const footprint = JSON.stringify(unit.transforms);
    const release = { id: `unit/${unitIndex}`, order: [unitIndex], atS: unit.releaseOffsetTicks * tickS, share: 1 };
    const group = groups.get(footprint);
    if (group === undefined) groups.set(footprint, [release]);
    else group.push(release);
  }
  return [...groups.values()];
}

/** Whole target histories correlate that target's contacts. */
export function orbitReleaseGroups(contactsByTick: readonly { readonly tickOffset: number; readonly expectedContacts: number }[], tickS: number,
  targets?: readonly { readonly id?: string; readonly weight: number; readonly hitsByTick: readonly number[] }[], expiryBeforeContact = false): readonly (readonly DotRelease[])[] {
  if (targets !== undefined) {
    const normalization = Math.max(1, Math.ceil(targets.reduce((max, target) => Math.max(max, target.weight), 0)));
    return targets.map((target, index) => target.hitsByTick.map((tick, contact) => ({
      id: `orbit/target/${index}/contact/${contact}`, order: [contact], atS: tick * tickS,
      share: target.weight / normalization, gates: [{ id: `pricing-field/orbit/${target.id ?? index}/${target.weight}/${normalization}`, p: target.weight / normalization }], ...(expiryBeforeContact ? { expiryBeforeContact: true } : {}),
    })));
  }
  return [contactsByTick.filter((bucket) => bucket.expectedContacts > 0).map((bucket, index) => ({ id: `orbit/contact/${index}`, order: [index], atS: bucket.tickOffset * tickS, share: bucket.expectedContacts, ...(expiryBeforeContact ? { expiryBeforeContact: true } : {}) }))];
}

/**
 * What a dot is worth over the applications that reach one target, refreshed,
 * relative to the same applications stacked (each its own copy): both
 * undiscounted, so the ratio is the one factor by which a stacked price
 * over-counts the dot. Exactly 1 when nothing repeats.
 *
 * An application is a release of the delivery's schedule: `releaseGroups` are
 * the releases of the stage's attack units, one group per set of units that
 * reach the same targets (units of one footprint repeated by a volley, or the
 * repeated contacts of an orbit). Each release repeats the delivery's own
 * schedule (one application where there is no delivery to read, an orbit's
 * contact), and a group's applications refresh one another: the refreshed dot
 * runs wherever any release can reach, the stacked one is worth each release by
 * its share. Groups share no target, so they add. The default, one release at
 * 0, is the delivery alone.
 *
 * PROVISIONAL: it takes every application of a group to reach the target the
 * previous one reached, as the CC `MAINTAINED` model does; units of different
 * footprints are taken to reach different targets, and targets that walk into a
 * zone later start a dot of their own, which this does not give back.
 */
export function dotRefreshRatio(delivery: Delivery | undefined, cadence: DotCadence, releaseGroups: readonly (readonly DotRelease[])[] = [[{ atS: 0, share: 1 }]], hostTickS?: number): number {
  return dotApplicationRefreshRatio(delivery === undefined ? INSTANT_SCHEDULE : deliveryApplicationSchedule(delivery, hostTickS), cadence, releaseGroups, hostTickS);
}

/** The same refresh rule for a walk's current delivery schedule, including nested attacks. */
export function dotApplicationRefreshRatio(applications: ApplicationSchedule, cadence: DotCadence, releaseGroups: readonly (readonly DotRelease[])[] = [[{ atS: 0, share: 1 }]], hostTickS?: number): number {
  const quantized = periodicCadence(cadence, hostTickS);
  const groups = applicationGroups(applications, releaseGroups);
  const timeline = effectApplicationTimeline(groups, cadence.durationS, cadence.tickS, hostTickS);
  const stacked = timeline.contacts * dotTickCount(quantized.tickS, quantized.durationS);
  return stacked > 0 ? timeline.ticks.reduce((sum, tick) => sum + tick.share, 0) / stacked : 0;
}

/** Compose every release/unit/contact with its delivery's application offsets before reading the timeline. */
export function applicationGroups(applications: ApplicationSchedule, releaseGroups: readonly (readonly DotRelease[])[] = [[{ atS: 0, share: 1 }]], probability = 1, project?: (application: DotRelease) => DotRelease): readonly (readonly DotRelease[])[] {
  return releaseGroups.map((group, groupIndex) => group.flatMap((release, releaseIndex) => {
    const inherited = release.gates ?? [];
    const releaseProbability = runtimeGateProbability(inherited);
    const share = releaseProbability > 0 ? release.share / releaseProbability : 0;
    const whole = Math.floor(share);
    const probabilities = [...Array.from({ length: whole }, () => 1), ...(share > whole ? [share - whole] : [])];
    return probabilities.flatMap((presence, copy) => applications.offsetsS.map((offset, i) => {
      const gates = applications.gates?.[i] ?? [];
      const gateProbability = runtimeGateProbability(gates);
      const residual = gateProbability > 0 ? probability / gateProbability : 0;
      const id = `${release.id ?? `${groupIndex}/${releaseIndex}`}/${copy}`;
      const application = {
        targetKey: release.targetKey, id: `${id}/${i}`, order: [...(release.order ?? [releaseIndex]), ...(applications.orders?.[i] ?? [i])], atS: release.atS + offset, share: 1,
        gates: [...inherited, { id: `${id}/occupancy`, p: presence * residual },
          ...gates.map((gate) => mapRuntimeRollIds(gate, (key) => key === 'pricing-field/hpFraction' ? key : `${id}/${key}`))],
      };
      return project === undefined ? application : project(application);
    }));
  }));
}

/** Merge event releases and unit/contact releases into the one target schedule. */
export function castReleaseGroups(groups: readonly (readonly DotRelease[])[], releases: readonly DotRelease[] = [{ atS: 0, share: 1 }]): readonly (readonly DotRelease[])[] {
  return groups.map((group) => releases.flatMap((release, index) => group.map((unit, unitIndex) => ({ ...unit, id: `${release.id ?? index}/${unit.id ?? `unit/${unitIndex}`}`, order: [index, ...(unit.order ?? [])], atS: release.atS + unit.atS, share: release.share * unit.share, gates: [...(release.gates ?? []), ...(unit.gates ?? [])] }))));
}

/** Damage is priced on the dummy basis: every later contact and tick lands in full. */
export function effectDiscountRate(kind: string, timeDiscountRate: number): number {
  return kind === 'damage' || kind === 'dot' || kind === 'element_status' || overlapModel(kind) === 'MAINTAINED' ? 0 : timeDiscountRate;
}
